import React, { FC, useContext } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Button, Form, Textfield } from "@natera/form";
import { FormField, ForgotEmail } from "@app/components";
import { ErrorContext } from "@app/provider";
import validateEmail from "@app/utils/validateEmail";

import "./signIn.scss";

const messages = defineMessages({
  oneTimeCodeEmailLabel: {
    id: "oneTimeCodeEmailLabel",
    defaultMessage: "Email Address",
  },
  oneTimeCodeEmailPlaceholder: {
    id: "oneTimeCodeEmailPlaceholder",
    defaultMessage: "Enter Your Email Address",
  },
  oneTimeCodeEmailInvalid: {
    id: "oneTimeCodeEmailInvalid",
    defaultMessage: "Please enter a valid email address. (username@domain)",
  },
  oneTimeCodeFieldEmpty: {
    id: "oneTimeCodeFieldEmpty",
    defaultMessage: "Please fill in this field",
  },
  oneTimeCodeSubmit: {
    id: "oneTimeCodeSubmit",
    defaultMessage: "Log in with a one-time code",
  },
});

interface OneTimeCodeFormProps {
  onSubmit: (email: string) => void;
  isLoading: boolean;
  errorMessage?: string;
  prefilledEmail?: string;
  usePlusSignInEmailAddress: boolean;
}

export const OneTimeCodeForm: FC<OneTimeCodeFormProps> = ({
  onSubmit,
  isLoading,
  errorMessage,
  prefilledEmail,
  usePlusSignInEmailAddress,
}) => {
  const intl = useIntl();
  const { setValidationError, getValidationError, clearValidationError } =
    useContext(ErrorContext);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearValidationError("email");

    const formData = new FormData(event.currentTarget);
    const email = formData.get("email")?.toString().trim();

    if (!email) {
      setValidationError(
        "email",
        intl.formatMessage(messages.oneTimeCodeFieldEmpty)
      );
      return;
    }

    if (!validateEmail(email, usePlusSignInEmailAddress)) {
      setValidationError(
        "email",
        intl.formatMessage(messages.oneTimeCodeEmailInvalid)
      );
      return;
    }

    onSubmit(email);
  };

  const handleEmailFocus = () => {
    clearValidationError("email");
  };

  return (
    <section className="one-time-code__form">
      <Form
        className="one-time-code__request-form"
        onSubmit={handleSubmit}
        noValidate
      >
        <FormField
          className="email-field"
          label={intl.formatMessage(messages.oneTimeCodeEmailLabel)}
          required
          error={getValidationError("email")}
          htmlFor="otp-email"
          withPadding
        >
          <Textfield
            placeholder={intl.formatMessage(
              messages.oneTimeCodeEmailPlaceholder
            )}
            outline
            id="otp-email"
            name="email"
            required
            defaultValue={prefilledEmail}
            onFocus={handleEmailFocus}
            autoComplete="email"
          />
        </FormField>
        {errorMessage && (
          <p className="one-time-code__error" role="alert">
            {errorMessage}
          </p>
        )}
        <div className="one-time-code__actions">
          <Button type="submit" unelevated disabled={isLoading}>
            {intl.formatMessage(messages.oneTimeCodeSubmit)}
          </Button>
        </div>
      </Form>
    </section>
  );
};
